import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  LinkActionTypes,
  LinkOnServerCreated,
  ThrowLinkError,
  FireLinkSuccess,
  LinkOnServerUpdated,
  LinkOnServerDeleted,
  LinksPageRequested,
  LinksPageToggleLoading,
  LinksPageCancelled,
  LinksPageLoaded,
  LinksRequested,
  LinksLoaded,
} from '../_actions/link.actions';
import { Router } from '@angular/router';
import { LinkService } from '../_services';
import { QueryParamsModel } from '../../_base/crud';

@Injectable()
export class LinkEffects {
  @Effect()
  loadLinksPage$ = this.actions$.pipe(
    ofType<LinksPageRequested>(LinkActionTypes.LinksPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(new LinksPageToggleLoading({ isLoading: true }));
      const requestToServer = this.link.findLinks(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new LinksPageCancelled();
            } else {
              this.store.dispatch(new ThrowLinkError({ error: response[0].error }));
              return new LinksPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            return new LinksPageLoaded({
              links: result.data,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  loadLinks$ = this.actions$.pipe(
    ofType<LinksRequested>(LinkActionTypes.LinksRequested),
    mergeMap(() => {
      return this.link.loadLinks().pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return of({ type: 'NO_ACTION' });
          } else {
            return new LinksLoaded({ links: res.data });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteLink$ = this.actions$.pipe(
    ofType<LinkOnServerDeleted>(LinkActionTypes.LinkOnServerDeleted),
    mergeMap(({ payload }) => {
      return this.link.deleteLink(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowLinkError({ error: res.error });
          } else {
            this.store.dispatch(new LinksRequested());
            return new FireLinkSuccess({ action_type: LinkActionTypes.LinkOnServerDeleted });
          }
        }),
      );
    }),
  );

  @Effect()
  updateLink$ = this.actions$.pipe(
    ofType<LinkOnServerUpdated>(LinkActionTypes.LinkOnServerUpdated),
    mergeMap(({ payload }) => {
      return this.link.updateLink(payload.link).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowLinkError({ error: res.error });
          } else {
            this.store.dispatch(new LinksRequested());
            return new FireLinkSuccess({ action_type: LinkActionTypes.LinkOnServerUpdated });
          }
        }),
      );
    }),
  );

  @Effect()
  createLink$ = this.actions$.pipe(
    ofType<LinkOnServerCreated>(LinkActionTypes.LinkOnServerCreated),
    mergeMap(({ payload }) => {
      return this.link.createLink(payload).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowLinkError({ error: res.error });
          } else {
            this.store.dispatch(new LinksRequested());
            return new FireLinkSuccess({ action_type: LinkActionTypes.LinkOnServerCreated });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private link: LinkService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
